import { Component, OnInit, OnDestroy, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { GroupService } from '../../../core/services/group.service';
import { Group } from '../../../core/models/group.model';
import { UserContextService, UserAccessContext } from '../../../core/services/user-context.service';

@Component({
  selector: 'app-group-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatMenuModule,
    MatDividerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTooltipModule,
    RouterModule
],
  templateUrl: './group-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./group-details.component.scss']
})
export class GroupDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  groupId: string = '';
  group: Group | null = null;
  filteredSubGroups: Group[] = [];
  groupLevel = 0;
  loading = false;
  canManage = false;

  subGroupSearchControl = new FormControl('');
  // subGroupColumns: string[] = ['name', 'path', 'members', 'subGroups', 'actions'];
  // subGroupPageSize = 10;
  paginatedSubGroups: Group[] = [];
  subGroupPageSize = 10;
  subGroupPageIndex = 0;

  // Dropdown
  openDropdownId: string | null = null;

  userContext: UserAccessContext | null = null;
  isSuperAdmin = false;
  isGroupAdmin = false;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private groupService: GroupService,
    private userContextService: UserContextService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initializeUserContext();
    this.setupSubGroupSearch();

    // Récupérer l'ID du groupe depuis les paramètres de route
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.groupId = params['id'];
      if (this.groupId && this.userContext) {
        this.loadGroupDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Fermeture dropdown au clic extérieur
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-wrapper')) {
      this.openDropdownId = null;
    }
  }

  toggleDropdown(id: string, event: Event): void {
    event.stopPropagation();
    this.openDropdownId = this.openDropdownId === id ? null : id;
  }

  closeDropdowns(): void {
    this.openDropdownId = null;
  }

  // Pagination
  get subGroupTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredSubGroups.length / this.subGroupPageSize));
  }

  get subGroupVisiblePages(): number[] {
    const total = this.subGroupTotalPages;
    const current = this.subGroupPageIndex + 1;
    const pages: number[] = [];
    const ELLIPSIS = -1;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    if (current > 3) pages.push(ELLIPSIS);

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push(ELLIPSIS);
    pages.push(total);

    return pages;
  }

  goToSubGroupPage(index: number): void {
    if (index < 0 || index >= this.subGroupTotalPages) return;
    this.subGroupPageIndex = index;
    this.updatePaginatedSubGroups();
  }

  onSubGroupPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.subGroupPageSize = +target.value;
    this.subGroupPageIndex = 0;
    this.updatePaginatedSubGroups();
  }

  private updatePaginatedSubGroups(): void {
    const start = this.subGroupPageIndex * this.subGroupPageSize;
    this.paginatedSubGroups = this.filteredSubGroups.slice(start, start + this.subGroupPageSize);
  }

  private initializeUserContext(): void {
    this.userContextService.context$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(context => {
      this.userContext = context;
      this.isSuperAdmin = this.userContextService.isSuperAdmin();
      this.isGroupAdmin = this.userContextService.isGroupAdmin();

      // console.log('=== CONTEXTE UTILISATEUR ===');
      // console.log('Super Admin:', this.isSuperAdmin, 'Group Admin:', this.isGroupAdmin);

      if (this.groupId) {
        this.loadGroupDetails();
      }
    });

    const token = localStorage.getItem('access_token');
    if (token) {
      this.userContextService.updateContext(token);
    }
  }

  private setupSubGroupSearch(): void {
    this.subGroupSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.filterSubGroups();
      });
  }

  private loadGroupDetails(): void {
    if (!this.groupId) return;

    this.loading = true;
    this.groupService.getGroup(this.groupId).subscribe({
      next: (group) => {
        this.group = group;
        this.groupLevel = this.calculateGroupLevel(group.path);
        this.canManage = this.userContextService.canManageGroup(group.path);

        // Enrichir les sous-groupes avec le nombre de membres
        if (group.subGroups && group.subGroups.length > 0) {
          this.enrichSubGroupsWithMemberCount(group.subGroups);
        }

        this.filterSubGroups();
        this.loading = false;

        // console.log('Groupe chargé:', group);
      },
      error: (error) => {
        console.error('Erreur chargement groupe:', error);
        this.showError('Erreur lors du chargement du groupe');
        this.loading = false;
        this.goBack();
      }
    });
  }

  private enrichSubGroupsWithMemberCount(subGroups: Group[]): void {
    subGroups.forEach(subGroup => {
      if (subGroup.membersCount === undefined && subGroup.id) {
        this.groupService.getGroupMembers(subGroup.id, 0, 1).subscribe({
          next: (response) => {
            subGroup.membersCount = response.totalElements || 0;
          },
          error: (err) => console.error(`Erreur chargement membres ${subGroup.name}:`, err)
        });
      }
    });
  }

  // FilterSubGroups appelle la pagination
  private filterSubGroups(): void {
    if (!this.group || !this.group.subGroups) {
      this.filteredSubGroups = [];
      this.paginatedSubGroups = [];
      return;
    }

    const searchTerm = this.subGroupSearchControl.value?.trim().toLowerCase();

    if (!searchTerm) {
      this.filteredSubGroups = [...this.group.subGroups];
    } else {
      this.filteredSubGroups = this.group.subGroups.filter(subGroup =>
        subGroup.name.toLowerCase().includes(searchTerm) ||
        subGroup.path.toLowerCase().includes(searchTerm)
      );
    }

    this.subGroupPageIndex = 0;
    this.updatePaginatedSubGroups();
  }

  private calculateGroupLevel(path: string): number {
    if (!path) return 0;
    return path.split('/').filter(p => p.length > 0).length - 1;
  }

  canManageGroup(groupPath: string): boolean {
    return this.userContextService.canManageGroup(groupPath);
  }

  clearSubGroupSearch(): void {
    this.subGroupSearchControl.setValue('');
  }

  goBack(): void {
    this.router.navigate(['/dashboard/groups']);
  }

  refreshGroup(): void {
    this.loadGroupDetails();
  }

  // Actions sur le groupe principal

  viewMembers(): void {
    if (!this.group) return;

    import('../manage-members-dialog/manage-members-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.ManageMembersDialogComponent, {
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          groupId: this.group!.id,
          groupName: this.group!.name,
          groupPath: this.group!.path,
          canManage: this.canManage
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Membres mis à jour avec succès');
          this.loadGroupDetails();
        }
      });
    });
  }

  manageAdmins(): void {
    if (!this.group || !this.isSuperAdmin) return;

    import('../manage-admins-dialog/manage-admins-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.ManageAdminsDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          groupId: this.group!.id,
          groupName: this.group!.name,
          groupPath: this.group!.path
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Admins mis à jour avec succès');
          this.loadGroupDetails();
        }
      });
    });
  }

  editGroup(): void {
    if (!this.group || !this.isSuperAdmin) return;

    import('../edit-group-dialog/edit-group-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.EditGroupDialogComponent, {
        width: '600px',
        maxWidth: '95vw',
        data: { group: this.group }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Groupe modifié avec succès');
          this.loadGroupDetails();
        } else if (result?.error) {
          this.showError(result.message || 'Erreur lors de la modification du groupe');
        }
      });
    });
  }

  createSubGroup(): void {
    if (!this.group) return;

    import('../create-group-dialog/create-group-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.CreateGroupDialogComponent, {
        width: '600px',
        maxWidth: '95vw',
        data: { parentGroup: this.group }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Sous-groupe créé avec succès');
          this.loadGroupDetails();
        } else if (result?.error) {
          this.showError(result.message || 'Erreur lors de la création du sous-groupe');
        }
      });
    });
  }

  // Actions sur les sous-groupes

  viewSubGroupDetails(subGroup: Group): void {
    this.router.navigate(['/groups', subGroup.id, 'details']);
  }

  viewSubGroupUsers(subGroup: Group): void {
    import('../view-group-users-dialog/view-group-users-dialog.component').then(m => {
      this.dialog.open(m.ViewGroupUsersDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          groupId: subGroup.id,
          groupName: subGroup.name,
          groupPath: subGroup.path
        }
      });
    });
  }

  manageSubGroupMembers(subGroup: Group): void {
    if (!this.canManageGroup(subGroup.path)) {
      this.showError('Vous n\'avez pas la permission de gérer les membres de ce groupe');
      return;
    }

    import('../manage-members-dialog/manage-members-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.ManageMembersDialogComponent, {
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          groupId: subGroup.id,
          groupName: subGroup.name,
          groupPath: subGroup.path,
          canManage: this.canManageGroup(subGroup.path)
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Membres mis à jour avec succès');
          this.loadGroupDetails();
        }
      });
    });
  }

  manageSubGroupAdmins(subGroup: Group): void {
    if (!this.isSuperAdmin) {
      this.showError('Seuls les Super Admins peuvent gérer les administrateurs');
      return;
    }

    import('../manage-admins-dialog/manage-admins-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.ManageAdminsDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          groupId: subGroup.id,
          groupName: subGroup.name,
          groupPath: subGroup.path
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Admins mis à jour avec succès');
          this.loadGroupDetails();
        }
      });
    });
  }

  createSubSubGroup(subGroup: Group): void {
    if (!this.isSuperAdmin) {
      this.showError('Seuls les Super Admins peuvent créer des sous-groupes');
      return;
    }

    import('../create-group-dialog/create-group-dialog.component').then(m => {
      const dialogRef = this.dialog.open(m.CreateGroupDialogComponent, {
        width: '600px',
        maxWidth: '95vw',
        data: { parentGroup: subGroup }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result?.success) {
          this.showSuccess('Sous-groupe créé avec succès');
          this.loadGroupDetails();
        } else if (result?.error) {
          this.showError(result.message || 'Erreur lors de la création du sous-groupe');
        }
      });
    });
  }

  editSubGroup(subGroup: Group): void {
    if (!this.isSuperAdmin) {
      this.showError('Seuls les Super Admins peuvent modifier des groupes');
      return;
    }

    this.groupService.getGroup(subGroup.id!).subscribe({
      next: (fullSubGroup) => {
        import('../edit-group-dialog/edit-group-dialog.component').then(m => {
          const dialogRef = this.dialog.open(m.EditGroupDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            data: { group: fullSubGroup }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
              this.showSuccess('Groupe modifié avec succès');
              this.loadGroupDetails();
            } else if (result?.error) {
              this.showError(result.message || 'Erreur lors de la modification du groupe');
            }
          });
        });
      },
      error: (error) => {
        console.error('Erreur:', error);
        this.showError('Erreur lors du chargement du groupe');
      }
    });
  }

  deleteSubGroup(subGroup: Group): void {
    if (!this.isSuperAdmin) {
      this.showError('Seuls les Super Admins peuvent supprimer des groupes');
      return;
    }

    const hasSubGroups = subGroup.subGroups && subGroup.subGroups.length > 0;
    const message = hasSubGroups
      ? `ATTENTION: Le groupe "${subGroup.name}" contient ${subGroup.subGroups!.length} sous-groupe(s).\n\nÊtes-vous sûr de vouloir supprimer ce groupe ET tous ses sous-groupes ?`
      : `Êtes-vous sûr de vouloir supprimer le groupe "${subGroup.name}" ?`;

    if (confirm(message)) {
      this.loading = true;
      this.groupService.deleteGroup(subGroup.id!).subscribe({
        next: () => {
          this.loading = false;
          this.showSuccess('Groupe supprimé avec succès');
          this.loadGroupDetails();
        },
        error: (error) => {
          this.loading = false;
          console.error('Erreur suppression:', error);
          this.showError(error.error?.message || 'Erreur lors de la suppression du groupe');
        }
      });
    }
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}